import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, LayoutDashboard, LogOut, Phone, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const Navbar = () => {
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const { user, logout, isAdmin, isExpert } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: 'About Us',            to: '/about' },
    { label: 'How It Works',        to: '/how-it-works' },
    { label: 'Experts',             to: '/experts' },
    { label: 'Completed Projects',  to: '/completed-projects' },
    // { label: 'Categories',   to: '/categories' },
    // { label: 'Help',         to: '/help' },
  ];

  // Where to go when user clicks "Dashboard"
  const dashboardPath = isAdmin
    ? '/admin'
    : isExpert
      ? '/expert/dashboard'
      : '/dashboard';

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '';

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    window.location.href = '/';
  };

  return (
    <>
      {/* ── Contact bar ── */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-forest-900 text-cream-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-end gap-6 h-8 text-xs">
          <a href="tel:+233533597614" className="flex items-center gap-1.5 text-cream-200 hover:text-white transition-colors">
            <Phone size={11} />
            +233 53 359 7614
          </a>
          <a href="mailto:support@tourenthub.com" className="flex items-center gap-1.5 text-cream-200 hover:text-white transition-colors">
            <Mail size={11} />
            support@tourenthub.com
          </a>
        </div>
      </div>

      <nav className={`fixed top-8 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled
        ? 'bg-cream-50/95 backdrop-blur-md shadow-sm border-b border-cream-200'
        : 'bg-black/20 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="flex items-baseline gap-0.5">
              <span className={`font-serif font-semibold text-xl transition-colors duration-300 ${scrolled ? 'text-forest-900' : 'text-white'}`}>
                Tourent
              </span>
              <span className={`font-serif font-semibold text-xl transition-colors duration-300 ${scrolled ? 'text-forest-500' : 'text-green-400'}`}>
                {' '}Hub
              </span>
            </div>
            <div className={`hidden sm:block text-[9px] tracking-[0.18em] uppercase font-sans border-l pl-3 ml-1 transition-colors duration-300 ${
              scrolled ? 'text-forest-600/50 border-forest-300' : 'text-white/50 border-white/30'
            }`}>
              Entrepreneur · Expertise · Growth
            </div>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className={`text-sm font-medium transition-colors duration-300 relative group ${
                  scrolled
                    ? 'text-forest-700 hover:text-forest-900'
                    : 'text-white/85 hover:text-white'
                }`}
              >
                {link.label}
                <span className={`absolute -bottom-0.5 left-0 w-0 h-0.5 transition-all duration-200 group-hover:w-full ${
                  scrolled ? 'bg-forest-500' : 'bg-green-400'
                }`} />
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              // ── Logged in ──────────────────────────────────
              <>
                <Link
                  to={dashboardPath}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors duration-300 px-4 py-2 rounded-full border ${
                    scrolled
                      ? 'text-forest-700 border-forest-200 hover:bg-forest-50'
                      : 'text-white/90 border-white/30 hover:bg-white/10'
                  }`}
                >
                  <LayoutDashboard size={15} />
                  Dashboard
                </Link>

                {/* Avatar + name */}
                <div className={`flex items-center gap-2 text-sm font-medium ${
                  scrolled ? 'text-forest-700' : 'text-white/90'
                }`}>
                  <div className="w-8 h-8 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 font-serif font-bold text-xs overflow-hidden flex-shrink-0">
                    {user.avatar
                      ? <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                      : initials}
                  </div>
                  <span className="hidden xl:block">{user.firstName}</span>
                </div>
              </>
            ) : (
              // ── Logged out ─────────────────────────────────
              <>
                <Link
                  to="/login"
                  className={`text-sm font-medium transition-colors duration-300 px-4 py-2 ${
                    scrolled ? 'text-forest-700 hover:text-forest-900' : 'text-white/85 hover:text-white'
                  }`}
                >
                  Sign In
                </Link>
                <Link to="/register" className="btn-rust text-sm px-5 py-2.5">
                  Get Started →
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`lg:hidden p-2 transition-colors duration-300 ${scrolled ? 'text-forest-700' : 'text-white'}`}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-cream-50 border-t border-cream-200 px-6 py-6 space-y-4 shadow-lg">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="block text-sm font-medium text-forest-700 hover:text-forest-900 py-2 transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}

          <div className="pt-4 border-t border-cream-200 flex flex-col gap-3">
            {user ? (
              <>
                {/* User info */}
                <div className="flex items-center gap-3 py-2">
                  <div className="w-9 h-9 bg-forest-700 rounded-full flex items-center justify-center text-cream-50 font-serif font-bold text-sm overflow-hidden flex-shrink-0">
                    {user.avatar
                      ? <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                      : initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-forest-900">{user.firstName} {user.lastName}</div>
                    <div className="text-xs text-forest-500 capitalize">{user.role?.toLowerCase().replace('_', ' ')}</div>
                  </div>
                </div>
                <Link
                  to={dashboardPath}
                  className="btn-outline text-center justify-center flex items-center gap-2"
                  onClick={() => setMenuOpen(false)}
                >
                  <LayoutDashboard size={14} /> Go to Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center gap-2 text-sm font-medium text-rust-500 border border-rust-200 rounded-full py-2.5 hover:bg-rust-50 transition-colors"
                >
                  <LogOut size={14} /> Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login"    className="btn-outline text-center justify-center" onClick={() => setMenuOpen(false)}>Sign In</Link>
                <Link to="/register" className="btn-rust justify-center"                onClick={() => setMenuOpen(false)}>Get Started →</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;